/// <amd-module name="VSS/Features/Frame/CommandBar/CommandBarFlyout" />
import React = require("react");
import { css, VssComponent } from "VSS/Platform/Layout";
import { FocusZone } from "VSSUI/FocusZone";

import { Callout, ContentLocation, ContentOrientation, ContentJustification } from "VSSUI/Callout";
import { ICommandBarFlyoutPaneProps } from "./CommandBarFlyout.Props";

/**
 * A CommandBarFlyout is a used to draw a callout that acts like a modal dialog. It captures the focus and
 * renders a view on top of the current page (generally along the left or right side.
 */
export class CommandBarFlyout extends VssComponent<ICommandBarFlyoutPaneProps, {}> {
    private calloutDismissed?: () => void;

    public render(): JSX.Element | null {
        // We need to inject our onDismiss method as a property to the children. This allows them
        // to dismiss the CommandBarFlyout using a component within the pane.
        const children = React.Children.map(this.props.children, (childComponent: React.ReactElement<any>, index: number) => {
            return (
                childComponent &&
                React.cloneElement(
                    childComponent,
                    Object.assign({}, childComponent.props, { onDismiss: this.onDismiss }),
                    childComponent.props.children
                )
            );
        });

        return (
            <Callout
                ariaLabel={this.props.ariaLabel}
                className={css("command-bar-flyout", this.props.className)}
                contentClassName={css("command-bar-flyout-content relative scroll-hidden", this.props.contentClassName)}
                contentJustification={ContentJustification.Stretch}
                contentLocation={ContentLocation.End}
                contentOrientation={ContentOrientation.Column}
                id={this.props.id}
                escDismiss={this.props.escDismiss}
                lightDismiss={this.props.lightDismiss}
                modal={this.props.modal}
                onDismiss={this.onDismiss}
            >
                <FocusZone
                    circularNavigation={true}
                    defaultActiveElement={this.props.defaultActiveElement || ".command-bar-flyout-focus-element"}
                    focusOnMount={true}
                    handleTabKey={true}
                    includeDefaults={true}
                >
                    <div aria-label={this.props.ariaLabel} className="flex flex-column flex-grow scroll-hidden">
                        <div className="command-bar-flyout-focus-element" role="presentation" tabIndex={-1} />
                        {children}
                    </div>
                </FocusZone>
            </Callout>
        );
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();

        // Call the layout managers dismissal function (this removes the CommandBarFlyout from the DOM).
        this.calloutDismissed && this.calloutDismissed();
    }

    private onDismiss = () => {
        // Call the component props dismissal function. The internal dismissal will get called when
        // the component is unmounted.
        this.props.onDismiss && this.props.onDismiss();
    };
}

VssComponent.register("commandBarFlyout", CommandBarFlyout);
